import { brand } from "@/config/brand";

const currentYear = new Date().getFullYear();

interface CrawlerContentProps {
  /** Page title for crawlers */
  title: string;
  /** Full page description */
  description: string;
  /** Semantic content sections — each rendered as a block with heading + body */
  sections?: { heading: string; body: string }[];
  /** Navigation links relevant to this page */
  links?: { label: string; href: string }[];
  /** Schema.org itemType for the wrapper */
  schemaType?: string;
  /** Additional key-value metadata */
  meta?: Record<string, string>;
}

/**
 * Renders hidden semantic HTML that crawlers and screen readers can parse,
 * plus a full `<noscript>` fallback for users/bots without JavaScript.
 *
 * Usage: Drop into any page component to guarantee full-text crawlability.
 */
const CrawlerContent = ({
  title,
  description,
  sections = [],
  links = [],
  schemaType = "https://schema.org/WebPage",
  meta = {},
}: CrawlerContentProps) => {
  return (
    <>
      {/* ── Hidden semantic HTML for JS-enabled crawlers ── */}
      <div
        aria-hidden="true"
        className="sr-only"
        role="complementary"
        itemScope
        itemType={schemaType}
      >
        <h2 itemProp="name">{title}</h2>
        <p itemProp="description">{description}</p>

        {sections.map((s, i) => (
          <section key={i} itemProp="hasPart" itemScope itemType="https://schema.org/WebPageElement">
            <h3 itemProp="name">{s.heading}</h3>
            <p itemProp="text">{s.body}</p>
          </section>
        ))}

      {/* ── WebMCP declarative tool markup ── */}
      <div
        data-tool-name={
          schemaType.includes("Product") ? "get_product_info" :
          schemaType.includes("Collection") ? "get_category_info" :
          schemaType.includes("SearchResults") ? "get_search_results" :
          "get_page_info"
        }
        data-tool-description={
          schemaType.includes("Product")
            ? "Get detailed product information including name, price, description, availability, and ratings"
            : schemaType.includes("Collection")
            ? "Get detailed information about this category page including description, subcategories, and product count"
            : schemaType.includes("SearchResults")
            ? "Get search results with product listings, filters, and sorting options"
            : "Get information about this page including title, description, and content sections"
        }
        data-tool-input-schema={JSON.stringify({
          type: "object",
          properties: {
            query: { type: "string", description: "Page identifier or search query", default: title },
          },
        })}
        data-tool-output-schema={JSON.stringify({
          type: "object",
          properties: {
            title: { type: "string" },
            description: { type: "string" },
            sections: { type: "array", items: { type: "object", properties: { heading: { type: "string" }, body: { type: "string" } } } },
            meta: { type: "object" },
          },
        })}
      >
        <span data-tool-result={JSON.stringify({ title, description, sections: sections.map(s => ({ heading: s.heading, body: s.body })), meta })} />
      </div>

        {links.length > 0 && (
          <nav>
            <ul>
              {links.map((l, i) => (
                <li key={i}>
                  <a href={l.href} tabIndex={-1}>{l.label}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {Object.entries(meta).map(([key, value]) => (
          <meta key={key} itemProp={key} content={value} />
        ))}
      </div>

      {/* ── Full noscript fallback ── */}
      <noscript>
        <div
          style={{
            fontFamily: "'Assistant', Arial, sans-serif",
            direction: "rtl",
            textAlign: "right",
            maxWidth: 900,
            margin: "0 auto",
            padding: 24,
            color: "#e0f2f1",
            background: "#0d1520",
          }}
        >
          <h1
            style={{
              fontSize: "2em",
              borderBottom: "2px solid #14b8a6",
              paddingBottom: 8,
              marginBottom: 16,
            }}
          >
            {title}
          </h1>
          <p style={{ fontSize: "1.1em", marginBottom: 24 }}>{description}</p>

          {sections.map((s, i) => (
            <div key={i} style={{ marginBottom: 24 }}>
              <h2
                style={{
                  fontSize: "1.3em",
                  color: "#14b8a6",
                  marginBottom: 8,
                }}
              >
                {s.heading}
              </h2>
              <p>{s.body}</p>
            </div>
          ))}

          {links.length > 0 && (
            <nav style={{ marginTop: 24 }}>
              <h3 style={{ marginBottom: 8 }}>ניווט</h3>
              {links.map((l, i) => (
                <a
                  key={i}
                  href={l.href}
                  style={{
                    display: "inline-block",
                    color: "#14b8a6",
                    marginLeft: 16,
                    marginBottom: 8,
                  }}
                >
                  {l.label}
                </a>
              ))}
            </nav>
          )}

          <footer
            style={{
              borderTop: "1px solid #1e3a4a",
              marginTop: 32,
              paddingTop: 16,
              fontSize: "0.9em",
              color: "#94a3b8",
            }}
          >
            <p>
              © {currentYear} {brand.name}. כל הזכויות שמורות.
            </p>
            <a href="/" style={{ color: "#14b8a6" }}>
              חזרה לדף הבית
            </a>
          </footer>
        </div>
      </noscript>
    </>
  );
};

export default CrawlerContent;
